import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { createUUID } from "@miplatform/utils";

export const useAccountAdderBucketsTabViewModel = ({
  stateManager,
  setEditMode,
  getValues,
  setValue,
  append,
  remove,
  reset,
}) => {
  const [adderBuckets, setAdderBuckets] = useState([]);
  const [adderOptions, setAdderOptions] = useState([]);
  const GET_ACCOUNT_ADDER_BUCKETS_UUID = createUUID();
  const GET_ADDER_OPTIONS_UUID = createUUID();
  const HANDLE_SAVE_ADDER_BUCKETS_UUID = createUUID();

  const { adders } = getValues();

  const getAccountAdderBuckets = (accountId) => {
    stateManager.abort(GET_ACCOUNT_ADDER_BUCKETS_UUID);
    stateManager.run(async () => {
      const response = await homeService.adders.accounts.getAll({
        accountId,
        sort: "displayOrder",
      });
      if (response.status === HTTP_STATUSES.OK) {
        const formattedAdders = response.data.adders.map(({ id }) => ({ id }));
        setAdderBuckets(formattedAdders);
        reset({ adders: formattedAdders });
      }
    }, GET_ACCOUNT_ADDER_BUCKETS_UUID);
  };

  const getAdderOptions = () => {
    stateManager.abort(GET_ADDER_OPTIONS_UUID);
    stateManager.run(async () => {
      const response = await homeService.adders.getAll({ sort: "name" });
      if (response.status === HTTP_STATUSES.OK) {
        const options = response.data.map((adder) => ({
          label: adder.name,
          value: adder.id,
        }));
        setAdderOptions(options);
      }
    }, GET_ADDER_OPTIONS_UUID);
  };

  const hasDuplicates = (data) => {
    const hasDuplicate = data.some((item, i) => data.indexOf(item) !== i);
    if (hasDuplicate) {
      toastService.error({ text: "Duplicate adders are not allowed." });
    }
    return hasDuplicate;
  };

  const handleSaveAdderBuckets = (data, accountId) => {
    const modifiedData = data
      .filter((item) => !item.removed)
      .map((item) => item.id);

    if (hasDuplicates(modifiedData)) {
      return;
    }

    stateManager.abort(HANDLE_SAVE_ADDER_BUCKETS_UUID);
    stateManager.run(async () => {
      const response = await homeService.adders.accounts.update({
        accountId,
        data: modifiedData,
        toastMessageConfig: {
          success: { text: "Successfully saved adder buckets." },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setEditMode(false);
        getAccountAdderBuckets(accountId);
      }
    }, HANDLE_SAVE_ADDER_BUCKETS_UUID);
  };

  const isRemoved = (index) => adders[index]?.removed;

  const handleAddAdder = () => append({ id: "", isNew: true });

  const handleRemoveAdder = (index) => {
    const { isNew, removed } = adders[index];

    if (isNew) {
      remove(index);
      return;
    }

    if (removed) {
      setValue(`adders[${index}].removed`, false, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue(`adders[${index}].removed`, true, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  return {
    isLoading: stateManager.isBusy(),
    adderBuckets,
    adderOptions,
    getAccountAdderBuckets,
    getAdderOptions,
    handleSaveAdderBuckets,
    isRemoved,
    handleAddAdder,
    handleRemoveAdder,
  };
};
